import { useEffect } from 'react';
import { angularInjectorProvider } from 'Injector';
import { useCurrentUser } from 'FrontRoyalAngular';
import { type DialogModal } from 'DialogModal';
import { getProgramApplication } from 'Users';
import { POST_APPLY_INTERVIEW_MODAL_SESSION_STORAGE_KEY, PostApplyInterviewModalType } from 'ProgramApplication';
import { ProgramTypeConfigs } from 'Program';

/**
 * Hook that shows the post-apply interview modal on the dashboard after application submission.
 *
 * ## Trigger Mechanism (sessionStorage)
 * `submit_application_form_dir.js` sets a one-time flag in sessionStorage when the user submits an
 * application the server deemed eligible for the modal (`postApplyInterviewModalType` is 'instant'
 * or 'optional'), then redirects to the dashboard. This hook consumes that flag once and clears it,
 * so the modal shows exactly once and not again on refresh.
 *
 * The flag is just a trigger — everything the modal needs is derived here from the freshly-applied
 * program application (the same source `studentDashboardConfig.ts` reads for suppression):
 * - locale keys: from the ProgramTypeConfig, keyed by the modal type
 * - Calendly calendar: the server-resolved `postApplyInterviewModalCalendlyUrl`, based on the program type
 *
 * ## Related: Modal Suppression Mechanism (database field)
 * The persistent `postApplyInterviewModalType` field on the ProgramApplication is used in
 * `studentDashboardConfig.ts` to suppress the generic "applicationReceived" modal for users who
 * saw a post-apply interview modal, so they don't see two modals.
 */
export function usePostApplyInterviewModal(): void {
    const currentUser = useCurrentUser();

    useEffect(() => {
        if (!sessionStorage.getItem(POST_APPLY_INTERVIEW_MODAL_SESSION_STORAGE_KEY)) return;

        // Wait until the current user (and its program application) is loaded before consuming the
        // flag, so a not-yet-loaded user doesn't clear the trigger before we can show the modal.
        const programApplication = currentUser ? getProgramApplication(currentUser) : null;
        if (!programApplication) return;

        // Consume the one-time flag now that the data is available, so it can't re-fire on refresh.
        sessionStorage.removeItem(POST_APPLY_INTERVIEW_MODAL_SESSION_STORAGE_KEY);

        const modalType = programApplication.postApplyInterviewModalType;
        if (modalType !== PostApplyInterviewModalType.Instant && modalType !== PostApplyInterviewModalType.Optional) {
            return;
        }

        const localeKeys =
            ProgramTypeConfigs[programApplication.programType].postApplyInterviewModalLocaleKeys[modalType];

        const DialogModal = angularInjectorProvider.get<DialogModal>('DialogModal');
        if (!DialogModal) return;

        DialogModal.alert({
            content:
                `<interview-modal` +
                ` next-step-key="${localeKeys.nextStepKey}"` +
                ` selectivity-message-key="${localeKeys.selectivityMessageKey}"` +
                ` calendly-base-url="${programApplication.postApplyInterviewModalCalendlyUrl ?? ''}"` +
                ` program-application-id="${programApplication.id}"` +
                `></interview-modal>`,
            classes: ['instant-interview-modal'],
        });
    }, [currentUser]);
}
